import type { InvestmentSummary } from "@eu.mdotm.api/sphere";
import AuthorizationGuard from "$root/components/AuthorizationGuard";
import { ButtonWithSelect } from "$root/components/buttons/ButtonWithSelect";
import { FakeAiLoaderBase } from "$root/components/FakeAiLoader";
import { IconWalls } from "$root/components/IconWall";
import { type MarkdownRendererProps, MarkdownRenderer } from "$root/components/MarkdownRenderer/MarkdownRenderer";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import { START_DATE_CREATE_PROPOSAL_COMMENTARY } from "$root/functional-areas/const";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import { differenceInDaysISO, qualifier } from "$root/utils";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import {
	ReapplySphereMetricsBanner,
	usePortfolioCommentary,
} from "$root/widgets-architecture/widgets/PortfolioCommentaryMDBlock";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { CircularProgressBar, Icon, ScrollWrapper, Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useContext, useMemo } from "react";
import { PortfolioWidgetBase, WidgetStatus } from "./PortfolioWidgetStatus";

export type PortfolioCommentaryProps = {
	portfolio: InvestmentSummary;
	enhanced: boolean;
};

export function PortfolioCommentary({ portfolio, enhanced }: PortfolioCommentaryProps): React.JSX.Element {
	const { formatDateTime } = useLocaleFormatters();
	const { asOf, asOfSnapshot } = useContext(PortfolioContext);
	//animation atom progress
	const {
		cachedCommentaryStatus,
		commentaryQuery,
		commentaryTemplatesQuery,
		fakeLoaderState,
		generateCommentaryNonBlocking,
		templateOptions,
	} = usePortfolioCommentary({ portfolio: portfolio ?? {}, enhanced, asOf, asOfSnapshot });

	const canUserGenerateNewCommentary = useMemo(() => {
		// Pinned to a past as-of date: generating would mutate the live commentary (R13).
		if (asOf != null) {
			return false;
		}
		if (enhanced) {
			return differenceInDaysISO(portfolio?.lastActionDate, START_DATE_CREATE_PROPOSAL_COMMENTARY) > 0;
		}

		return true;
	}, [asOf, enhanced, portfolio?.lastActionDate]);

	const generateCommentaryAction = useMemo(() => {
		if (commentaryQuery.data && portfolio) {
			return (
				<AuthorizationGuard
					permissionChecker={aclByArea.portfolio.canEditComposition}
					acl={portfolio?.richAcl?.acl ?? []}
				>
					{canUserGenerateNewCommentary && (
						<ButtonWithSelect
							options={templateOptions}
							value={commentaryQuery.data?.template?.uuid}
							disabled={
								portfolio?.status === "CALCULATING" ||
								portfolio?.status === "ERROR" ||
								commentaryQuery.data?.status === "CALCULATING" ||
								cachedCommentaryStatus === "CALCULATING"
							}
							palette="secondary"
							size="small"
							onClick={generateCommentaryNonBlocking}
							enableSearch
							dataAttributes={{
								buttonDataAttributes: { "data-qualifier": qualifier.widgets.portfolioCommentary.generate },
								selectDataAttributes: { "data-qualifier": qualifier.widgets.portfolioCommentary.selectTemplate },
								selectTriggerDataAttributes: {
									"data-qualifier": qualifier.widgets.portfolioCommentary.selectTemplate + "/Trigger",
								},
							}}
						>
							Generate
						</ButtonWithSelect>
					)}
				</AuthorizationGuard>
			);
		} else {
			return <></>;
		}
	}, [
		cachedCommentaryStatus,
		canUserGenerateNewCommentary,
		commentaryQuery.data,
		generateCommentaryNonBlocking,
		portfolio,
		templateOptions,
	]);

	const { push } = useTypedNavigation();
	const widgetDataQuery = useMappedQuery(
		(commentaryData) => {
			if (
				(commentaryData.status === "CALCULATING" || portfolio?.status === "CALCULATING") &&
				!commentaryData.commentary
			) {
				return {
					data: undefined,
					widgetStatus: WidgetStatus.CALCULATING as const,
				};
			}
			if (!commentaryData.commentary) {
				return {
					data: { ...commentaryData, templates: commentaryTemplatesQuery.data ?? [] },
					widgetStatus: WidgetStatus.EMPTY as const,
				};
			}
			return {
				data: { ...commentaryData, templates: commentaryTemplatesQuery.data ?? [] },
				widgetStatus: WidgetStatus.READY as const,
			};
		},
		[commentaryTemplatesQuery.data, portfolio?.status],
		commentaryQuery,
	);

	return (
		<div className="bg-white rounded-sm min-h-0 flex flex-col h-[calc(100dvh-180px)]">
			{cachedCommentaryStatus === "CALCULATING" && <FakeAiLoaderBase state={fakeLoaderState} />}
			<ReactQueryWrapperBase
				query={widgetDataQuery}
				loadingFallback={<IconWalls.CalculatingApi />}
				errorFallback="Generative AI commentary is not available at the moment. It will be generated soon!"
			>
				{(widgetData, { isFetching }) => (
					<PortfolioWidgetBase
						{...widgetData}
						iconWalls={{
							empty: IconWalls.PortfolioCommentaryEmpty({
								onGenerate: (templateId) => {
									if (templateId) {
										generateCommentaryNonBlocking(templateId);
										return;
									}

									platformToast({
										children: "Unable to generate portfolio commentary",
										severity: "error",
										icon: "Ask-ai",
									});
								},
								generateOption: templateOptions,
								defaultValue: commentaryQuery.data?.template?.uuid,
							}),
						}}
					>
						{({
							commentary,
							commentaryDate,
							usedCustomMetrics,
							metadataLastUpdate,
							prevUsedCustomMetrics,
							template,
						}) => (
							<>
								<div
									className="flex py-2 px-4 border-b justify-between"
									style={{ borderColor: themeCSSVars.palette_N200 }}
								>
									<div className="flex items-center">
										<Text type="Body/M/Bold" color={themeCSSVars.palette_N500}>
											Template: {template?.name}
										</Text>
									</div>
									<div className="flex gap-2 items-center">
										<AuthorizationGuard requiredServices={["COMMENTARY_BUILDER"]}>
											<Button
												size="small"
												palette="tertiary"
												onClick={() =>
													push("Storyfolio/Details", {
														uuid:
															templateOptions.find((x) => x.value === template?.uuid)?.value ??
															templateOptions.find((x) => x.label === "Standard Template")?.value ??
															template?.uuid ??
															"",
													})
												}
											>
												Edit template
											</Button>
										</AuthorizationGuard>
										{generateCommentaryAction}
									</div>
								</div>
								<ScrollWrapper direction="vertical" startShadow>
									<div className="flex flex-col items-center pt-7 pb-4 overflow-y-auto">
										<div className="flex flex-col max-w-[946px]">
											<div className="flex justify-end">
												<div className="flex gap-4">
													{commentaryDate && (
														<div className="flex space-x-1 items-center">
															<Icon icon="Clock" color={themeCSSVars.palette_N300} size={16} />
															<Text type="Body/S/Book" as="p">
																Generated on&nbsp;
																<span data-qualifier={qualifier.widgets.portfolioCommentary.timeStamp}>
																	{formatDateTime(commentaryDate)}
																</span>
															</Text>
														</div>
													)}
												</div>
											</div>

											<div className="px-4" data-qualifier={qualifier.widgets.portfolioCommentary.content}>
												{isFetching ? (
													<div className="h-80 flex">
														<CircularProgressBar value="indeterminate" className="m-auto" />
													</div>
												) : (
													<>
														{(usedCustomMetrics || prevUsedCustomMetrics) && (
															<ReapplySphereMetricsBanner
																className="my-4"
																portfolio={portfolio}
																metadataLastUpdate={metadataLastUpdate}
															/>
														)}
														<MarkdownRenderer componentOverrides={markdownOverrides}>
															{commentary ?? ""}
														</MarkdownRenderer>
													</>
												)}
											</div>
										</div>
									</div>
								</ScrollWrapper>
							</>
						)}
					</PortfolioWidgetBase>
				)}
			</ReactQueryWrapperBase>
		</div>
	);
}

const markdownOverrides: MarkdownRendererProps["componentOverrides"] = {
	h1: ({ node: _node, ...props }) => (
		<h1 className="text-[18px] font-bold pb-2 pt-2 " {...props}>
			{props.children}
		</h1>
	),
	h2: ({ node: _node, ...props }) => (
		<h2 className="text-[18px] font-bold pb-2 pt-2" {...props}>
			{props.children}
		</h2>
	),
	h3: ({ node: _node, ...props }) => (
		<h3 className="text-[16px] font-bold pb-2 pt-2" {...props}>
			{props.children}
		</h3>
	),
	ul: ({ node: _node, ...props }) => <ul className="list-disc pl-4 text-[16px]" {...props} />,
	ol: ({ node: _node, ...props }) => <ol className="list-decimal pl-4 text-[16px]" {...props} />,
	li: ({ node: _node, ...props }) => <li className="text-[16px]" {...props} />,
	p: ({ node: _node, ...props }) => <div className="text text-[16px] pb-2" {...props} />,
	blockquote: ({ node: _node, ...props }) => (
		<blockquote className="bg-sky-100 px-2 py-1 rounded-sm text-[16px]" {...props} />
	),
	table: ({ node: _node, ...props }) => <table className="w-full border-collapse" {...props} />,
	thead: ({ node: _node, ...props }) => <thead {...props} />,
	tr: ({ node: _node, ...props }) => (
		<tr className={`even:bg-[#F7F8F9] border-b border-b-[color:var(--palette-N100)]`} {...props} />
	),
	td: ({ node: _node, ...props }) => <td className="text-left p-4 first:w-2/4" {...props} />,
	th: ({ node: _node, ...props }) => (
		<th className="text-left px-4 py-2 font-bold! text-[12px]! uppercase! text-[#667085]" {...props} />
	),
	tbody: ({ node: _node, ...props }) => <tbody {...props} />,
	code: ({ node: _node, ...props }) => <span className="text-[16px]" {...props} />,
	pre: ({ node: _node, ...props }) => <i className="text-[16px]" {...props} />,
};
